import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, EmptyState } from "../../../components/ui";
import {
  createMerchandiseDraft,
  draftsToMerchandise,
  merchandiseToDrafts,
  validateMerchandiseDrafts,
  type MerchandiseDraft,
  type MerchandiseDraftErrors,
} from "../../../features/shipments/merchandiseDraft";
import { useUpdateShipment } from "../../../features/shipments/mutations";
import { normalizeMerchandise, type Shipment } from "../../../features/shipments/types";
import { shipmentUpdateErrorKey } from "../utils/shipmentUnit";
import MerchandiseEditor from "./MerchandiseEditor";

interface ShipmentMerchandiseCardProps {
  shipment: Shipment;
}

export default function ShipmentMerchandiseCard({ shipment }: ShipmentMerchandiseCardProps) {
  const { t } = useTranslation("envios");
  const updateShipment = useUpdateShipment();
  const items = normalizeMerchandise(shipment.merchandise);

  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<MerchandiseDraft[]>([]);
  const [errors, setErrors] = useState<Record<string, MerchandiseDraftErrors>>({});

  const startEditing = () => {
    setDrafts(items.length > 0 ? merchandiseToDrafts(items) : [createMerchandiseDraft()]);
    setErrors({});
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setErrors({});
  };

  const save = () => {
    const found = validateMerchandiseDrafts(drafts);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error(t("merchandise.fixErrors"));
      return;
    }

    updateShipment.mutate(
      { id: shipment.id, data: { merchandise: draftsToMerchandise(drafts) } },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success(t("merchandise.saved"));
        },
        onError: (error) =>
          toast.error(t(`updateErrors.${shipmentUpdateErrorKey(error)}`)),
      },
    );
  };

  return (
    <div className="rounded-2xl border border-line p-5">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <i className="ri-box-3-line text-lg text-content" />
          <h3 className="text-sm font-bold text-content">{t("merchandise.title")}</h3>
        </div>
        {!editing && (
          <Button
            variant="outline"
            size="sm"
            icon={<i className="ri-edit-line" />}
            onClick={startEditing}
          >
            {t("merchandise.edit")}
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-4">
          <MerchandiseEditor
            drafts={drafts}
            onChange={setDrafts}
            errors={errors}
            disabled={updateShipment.isPending}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={updateShipment.isPending}
              icon={<i className="ri-check-line" />}
              onClick={save}
            >
              {t("merchandise.save")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={updateShipment.isPending}
              onClick={cancel}
            >
              {t("merchandise.cancel")}
            </Button>
          </div>
        </div>
      ) : items.length > 0 ? (
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={item.id ?? index} className="rounded-lg bg-subtle p-3">
              <p className="text-sm font-medium text-content">{item.description}</p>
              <p className="mt-0.5 text-xs text-content-muted">
                {[
                  t("merchandise.summary", {
                    quantity: item.quantity,
                    unitType: item.unitType,
                  }),
                  item.weight !== undefined && `${item.weight} kg`,
                  item.volume !== undefined && `${item.volume} m³`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon="ri-box-3-line" title={t("merchandise.empty")} />
      )}
    </div>
  );
}
